// @flow

import * as React from 'react';
import ParentScroll from '@ta/events.parent-scroll';
import WindowScroll from '@ta/events.window-scroll';

type Props = {|
  /** A number to specify a pixel-count fuzz-factor for determining visibility */
  +threshold: number,

  /** Callback that will be given the new visibility state when it changes */
  +onChange: ?(boolean) => void,

  /**
   * Render prop providing a target function (which should be attached to the element you are
   * interested in the visibility of as a ref), and the current visibility as a bool
   */
  +children: ((?HTMLElement) => void, boolean, ?HTMLElement) => React.Node,

  /**
   * Callback to grab a ref to the html element input
   */
  onRef?: (elem: ?HTMLElement) => mixed,

  /** disables listeners when true. This stops the chatty scroll handlers once you don't need to be notified anymore */
  disableListeners?: boolean,
|};

type State = {|
  visible: boolean,
|};

// Given two rectangles of coordinates, compute the rectangle that is the intersection of them.
// This code is willing to compute negative intersections, but that shouldn't be relevant here.
const intersectRect = (r1, r2) => ({
  top: Math.max(r1.top, r2.top),
  bottom: Math.min(r1.bottom, r2.bottom),
  right: Math.min(r1.right, r2.right),
  left: Math.max(r1.left, r2.left),
});

const getViewportRect = () => ({
  top: 0,
  left: 0,
  /* eslint-disable no-restricted-globals */
  right: window.innerWidth || 0,
  bottom: window.innerHeight || 0,
  /* eslint-enable no-restricted-globals */
});

// A rect may have a visible component if it has integer height AND width.
const checkRectHasSize = ({ top, bottom, left, right }) => bottom - top > 0 && right - left > 0;

// When called on an element outside of the DOM, IE<=11 throws 'Unspecified error' instead of returning a DOMRect object
// w/ all values zeroed like in other modern browsers.
const safeGetBoundingClientRect = el =>
  (el.parentNode && el.getBoundingClientRect()) || { top: 0, bottom: 0, left: 0, right: 0 };

const getParentElements = el => {
  const parents = [];
  let parent = el.parentNode;
  while (parent !== null) {
    // $FlowFixMe: Flow doesn't do refinements from loop conditions
    if (parent instanceof Element) {
      parents.push(parent);
    }
    // $FlowFixMe: Flow doesn't do refinements from loop conditions
    parent = parent.parentNode;
  }
  return parents;
};

const expandRect = (rect, threshold) => ({
  top: rect.top - threshold,
  left: rect.left - threshold,
  right: rect.right + threshold,
  bottom: rect.bottom + threshold,
});

// Currently this only attempts to check whether or not the element is consuming space in the page
// There's no attempt to check css visibility or opacity, nor whether or not something is covering
// the element you're interested in. It does, however, check all parents.
// It also accepts a threshold argument which will expand each rect by that amount in all directions
const isElementInView = (el, threshold) => {
  if (!el) {
    return false;
  }

  const baseRect = intersectRect(
    expandRect(getViewportRect(), threshold),
    safeGetBoundingClientRect(el),
  );
  // Quick short-circuit here for the common case when the element isn't in the viewport.
  if (!checkRectHasSize(baseRect)) {
    return false;
  }

  // Next, we need to collect all parents, and confirm that there's a non-zero intersection between
  // each bounding rect of the parents and the element itself (also the viewport, as done above)
  return checkRectHasSize(
    getParentElements(el)
      .map(parent => expandRect(safeGetBoundingClientRect(parent), threshold))
      .reduce((intersection, rect) => intersectRect(intersection, rect), baseRect),
  );
};

/**
 * The IsVisible component provides both a render prop as well as a callback interface to notify
 * downstream components when they become visible to the user, by looking at scroll position.
 *
 * Please note, this can be expensive, so make sure to get rid of this component from your
 * heirarchy when you no longer care about this information (after your component as been seen?).
 * For an example of this, take a look at @ta/media.image/LazyImage
 */
export default class IsVisible extends React.Component<Props, State> {
  static defaultProps = {
    threshold: 0,
    onChange: null,
    disableListeners: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = { visible: false };
    this.elmt = null;
  }

  componentDidMount() {
    this.checkVisibility();
  }

  checkVisibility = () => {
    // TODO: TRVX-7429 - IE11 never gets the updates on scroll
    const inView = isElementInView(this.elmt, this.props.threshold);
    if (inView !== this.state.visible) {
      this.setState({ visible: inView });
      if (this.props.onChange) {
        this.props.onChange(inView);
      }
    }
  };

  elmt: ?HTMLElement;

  render() {
    return (
      <React.Fragment>
        {this.props.disableListeners ? null : (
          <WindowScroll callback={this.checkVisibility} throttle={0} />
        )}
        <ParentScroll
          callback={this.checkVisibility}
          disableListeners={this.props.disableListeners}
        >
          {target =>
            this.props.children(
              ref => {
                target(ref);
                this.elmt = ref;
                if (this.props.onRef) {
                  this.props.onRef(ref);
                }
              },
              this.state.visible,
              this.elmt,
            )
          }
        </ParentScroll>
      </React.Fragment>
    );
  }
}
